// src/pages/InquiryAdminV2/index.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import LeftMenu from "../../components/left-menu";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

const MODES = [
  { value: "ungradedLegacy", label: "Legacy Ungraded" },
  { value: "stage1", label: "Stage 1 - New" },
  { value: "stage2", label: "Stage 2 - Nurture" },
  { value: "stage3", label: "Stage 3 - Qualified" },
  { value: "stage4", label: "Stage 4 - Paid" },
  { value: "bad", label: "Stage X - Bad" },
  { value: "cekatFailed", label: "Cekat Failed" },
  { value: "v2", label: "All V2" },
];

const LEAD_STAGE_OPTIONS = [
  { value: "S1_NEW", label: "Stage 1 - New Lead" },
  { value: "S2_RESPONSIVE", label: "Stage 2 - Responsive / Nurture" },
  { value: "S3_QUALIFIED", label: "Stage 3 - Qualified / Sales" },
  { value: "SX_BAD", label: "Stage X - Bad Lead" },
];

const SALES_OPTIONS = ["", "Diva", "Dinda"];

const NURTURE_DROP_REASONS = [
  { value: "", label: "Tidak ada / bukan drop" },
  { value: "NO_REPLY_AFTER_FU", label: "Tidak balas setelah FU" },
  { value: "NOT_READY_YET", label: "Belum siap" },
  { value: "PRICE_TOO_HIGH", label: "Harga terlalu tinggi" },
  { value: "STILL_CONSIDERING", label: "Masih pertimbangan" },
  { value: "NEED_LATER", label: "Butuh nanti" },
  { value: "WAITING_FOR_PARTNER", label: "Menunggu partner/boss" },
  { value: "BUDGET_NOT_READY", label: "Budget belum siap" },
  { value: "WRONG_TIMING", label: "Timing belum pas" },
];

const BAD_DROP_REASONS = [
  { value: "SPAM_GAJELAS", label: "Spam / gajelas" },
  { value: "ASKING_FOR_MONEY", label: "Minta uang / pinjam uang" },
  { value: "WRONG_NUMBER", label: "Nomor salah" },
  { value: "JOB_SEEKER", label: "Cari kerja" },
  { value: "PARTNERSHIP_ONLY", label: "Partnership saja" },
  { value: "COMPETITOR", label: "Kompetitor" },
  { value: "NOT_RELEVANT", label: "Tidak relevan" },
  { value: "ABUSIVE", label: "Kasar / abusive" },
];

const SORT_OPTIONS = [
  { value: "InqDate", label: "Inquiry Date" },
  { value: "lastActivityAt", label: "Last Activity" },
  { value: "leadStageUpdatedAt", label: "Stage Updated" },
  { value: "qualifiedAt", label: "Qualified At" },
  { value: "paidAt", label: "Paid At" },
];

const getStageLabel = (stage) => {
  if (stage === "S1_NEW") return "S1 New";
  if (stage === "S2_RESPONSIVE") return "S2 Nurture";
  if (stage === "S3_QUALIFIED") return "S3 Qualified";
  if (stage === "S4_WON") return "S4 Paid";
  if (stage === "SX_BAD") return "SX Bad";
  return stage || "Legacy";
};

const getStageChipColor = (stage) => {
  if (stage === "S1_NEW") return "default";
  if (stage === "S2_RESPONSIVE") return "warning";
  if (stage === "S3_QUALIFIED") return "success";
  if (stage === "S4_WON") return "primary";
  if (stage === "SX_BAD") return "error";
  return "default";
};

const getCekatChipColor = (status) => {
  if (status === "SENT") return "success";
  if (status === "FAILED") return "error";
  if (status === "NOT_ATTEMPTED") return "warning";
  if (status === "NOT_REQUIRED") return "default";
  return "default";
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const cleanPhoneForWa = (phone) => String(phone || "").replace(/\D/g, "");

const getAdminAuthConfig = () => {
  const token = process.env.REACT_APP_ADMIN_PIPELINE_TOKEN || "";

  if (!token) return {};

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export default function InquiryAdminV2() {
  const user = useSelector((state) => state?.user?.sales || "Reinner");

  const [mode, setMode] = useState("ungradedLegacy");
  const [q, setQ] = useState("");
  const [adcode, setAdcode] = useState("");
  const [sales, setSales] = useState("");
  const [sortBy, setSortBy] = useState("InqDate");

  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [gradeOpen, setGradeOpen] = useState(false);
  const [gradeStage, setGradeStage] = useState("S2_RESPONSIVE");
  const [gradeReason, setGradeReason] = useState("");
  const [gradeNote, setGradeNote] = useState("");
  const [dropReason, setDropReason] = useState("");
  const [manualSales, setManualSales] = useState("");
  const [notifySales, setNotifySales] = useState(true);
  const [gradeSaving, setGradeSaving] = useState(false);
  const [gradeError, setGradeError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailData, setDetailData] = useState(null);

  const selectedStageNeedsBadReason = gradeStage === "SX_BAD";
  const selectedStageIsStage3 = gradeStage === "S3_QUALIFIED";
  const selectedStageIsStage2 = gradeStage === "S2_RESPONSIVE";

  const currentDropOptions = useMemo(() => {
    if (selectedStageNeedsBadReason) return BAD_DROP_REASONS;
    if (selectedStageIsStage2) return NURTURE_DROP_REASONS;
    return [{ value: "", label: "Tidak perlu" }];
  }, [selectedStageNeedsBadReason, selectedStageIsStage2]);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");

      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inquiryListV2`,
        {
          params: {
            mode,
            q: q || undefined,
            adcode: adcode || undefined,
            sales: sales || undefined,
            sortBy,
            page,
            limit,
          },
          ...getAdminAuthConfig(),
        }
      );

      const data = resp.data || {};

      setRows(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching inquiry list v2:", err);
      setLoadError(
        err?.response?.data?.error ||
          err?.message ||
          "Gagal memuat inquiry list."
      );
    } finally {
      setLoading(false);
    }
  }, [mode, q, adcode, sales, sortBy, page, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchList();
  };

  const handleResetFilters = () => {
    setQ("");
    setAdcode("");
    setSales("");
    setSortBy("InqDate");
    setPage(1);
  };

  const openGradeDialog = (inq, defaultStage = "") => {
    setSelectedInquiry(inq);

    const nextStage =
      defaultStage ||
      (inq?.leadStage && inq.leadStage !== "S4_WON"
        ? inq.leadStage
        : "S2_RESPONSIVE");

    setGradeStage(nextStage);
    setGradeReason("");
    setGradeNote("");
    setDropReason("");
    setManualSales(inq?.currentOwnerSales || inq?.Sales || "");
    setNotifySales(true);
    setGradeError("");
    setGradeOpen(true);
  };

  const closeGradeDialog = () => {
    if (gradeSaving) return;
    setGradeOpen(false);
    setSelectedInquiry(null);
  };

  const handleGradeSubmit = async () => {
    if (!selectedInquiry?._id) return;

    if (selectedStageNeedsBadReason && !dropReason) {
      setGradeError("Drop reason wajib dipilih untuk Stage X.");
      return;
    }

    try {
      setGradeSaving(true);
      setGradeError("");

      const payload = {
        inquiryId: selectedInquiry._id,
        leadStage: gradeStage,
        actorId: user || "Reinner",
        reason:
          gradeReason ||
          `Manual grading to ${getStageLabel(gradeStage)}`,
        noteText: gradeNote,
        manualSales: selectedStageIsStage3 ? manualSales : "",
        notifySales: selectedStageIsStage3 ? notifySales : false,
        dropReason:
          selectedStageNeedsBadReason || selectedStageIsStage2
            ? dropReason
            : "",
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/manualGradeInquiryV2`,
        payload,
        getAdminAuthConfig()
      );

      console.log("manualGradeInquiryV2 response:", resp.data);

      setGradeOpen(false);
      setSelectedInquiry(null);
      await fetchList();
    } catch (err) {
      console.error("Error manual grading inquiry:", err);
      setGradeError(
        err?.response?.data?.error ||
          err?.message ||
          "Gagal manual grade inquiry."
      );
    } finally {
      setGradeSaving(false);
    }
  };

  const openDetailDialog = async (inq) => {
    if (!inq?._id) return;

    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError("");
      setDetailData(null);

      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inquiryDetailV2/${inq._id}`,
        getAdminAuthConfig()
      );

      setDetailData(resp.data || null);
    } catch (err) {
      console.error("Error fetching inquiry detail:", err);
      setDetailError(
        err?.response?.data?.error ||
          err?.message ||
          "Gagal memuat detail inquiry."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailDialog = () => {
    setDetailOpen(false);
    setDetailData(null);
    setDetailError("");
  };

  const handleExportCsv = () => {
    if (!rows.length) {
      window.alert("Tidak ada data untuk diexport.");
      return;
    }

    const exportRows = rows.map((r) => ({
      Nama: r.Nama || "",
      Phone: r.Phone || "",
      Stage: r.leadStage || "Legacy",
      Pipeline: r.pipelineVersion || "",
      Sales: r.currentOwnerSales || r.Sales || "",
      AdCode:
        r.qualificationAdCode ||
        r.latestAdCode ||
        r.firstAdCode ||
        r.AdCode ||
        "",
      ProdukInterest: r.ProdukInterest || "",
      LokasiInterest: r.LokasiInterest || "",
      CekatStatus: r.cekatGreetingStatus || "",
      CekatError: r.cekatGreetingError || "",
      InqDate: r.InqDate ? formatDateTime(r.InqDate) : "",
      LastActivity: r.lastActivityAt ? formatDateTime(r.lastActivityAt) : "",
      LatestDealStatus:
        r.latestDeal?.opportunityStatus || r.latestDeal?.status || "",
      LatestActivity: r.latestActivity?.note || "",
    }));

    const header = Object.keys(exportRows[0]);

    const csvLines = [
      header.join(","),
      ...exportRows.map((row) =>
        header
          .map((key) => {
            const cell = (row[key] ?? "").toString().replace(/"/g, '""');
            return `"${cell}"`;
          })
          .join(",")
      ),
    ];

    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", `inquiry_admin_v2_${mode}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderModeTabs = () => (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
      {MODES.map((item) => (
        <Button
          key={item.value}
          variant={mode === item.value ? "contained" : "outlined"}
          size="small"
          onClick={() => handleModeChange(item.value)}
        >
          {item.label}
        </Button>
      ))}
    </Stack>
  );

  const renderFilters = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Search nama / phone / produk / notes"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="AdCode"
            value={adcode}
            onChange={(e) => setAdcode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Sales</InputLabel>
            <Select
              label="Sales"
              value={sales}
              onChange={(e) => {
                setSales(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">Semua</MenuItem>
              {SALES_OPTIONS.filter(Boolean).map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort</InputLabel>
            <Select
              label="Sort"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              {SORT_OPTIONS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>

            <Button variant="outlined" onClick={handleResetFilters}>
              Reset
            </Button>

            <Button
              variant="outlined"
              onClick={handleExportCsv}
              disabled={!rows.length}
            >
              Export
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderTable = () => (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Inquiry Admin V2
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Total: {total} | Page {page} / {totalPages}
            </Typography>
          </Box>

          {loading && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Nama</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cekat</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>AdCode</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Produk / Lokasi</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sales / Deal</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3, textAlign: "center" }}
                  >
                    Tidak ada data.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((inq) => {
                const waPhone = cleanPhoneForWa(inq.Phone);

                const effectiveAdCode =
                  inq.qualificationAdCode ||
                  inq.latestAdCode ||
                  inq.firstAdCode ||
                  inq.AdCode ||
                  "";

                const latestDealStatus =
                  inq.latestDeal?.opportunityStatus ||
                  inq.latestDeal?.status ||
                  "-";

                const latestDealSales =
                  inq.latestDeal?.Sales ||
                  inq.currentOwnerSales ||
                  inq.Sales ||
                  "-";

                return (
                  <TableRow key={inq._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {inq.Nama || "-"}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {inq.Email || ""}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{inq.Phone || "-"}</Typography>

                      {!!waPhone && (
                        <Button
                          size="small"
                          onClick={() =>
                            window.open(
                              `https://wa.me/${waPhone}`,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        >
                          WA
                        </Button>
                      )}
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={getStageLabel(inq.leadStage)}
                          color={getStageChipColor(inq.leadStage)}
                        />

                        {inq.isManuallyGraded && (
                          <Typography variant="caption" color="text.secondary">
                            Manual by {inq.manuallyGradedBy || "-"}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={inq.cekatGreetingStatus || "-"}
                          color={getCekatChipColor(inq.cekatGreetingStatus)}
                        />

                        {!!inq.cekatGreetingError && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              display: "block",
                              maxWidth: 220,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {inq.cekatGreetingError}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {effectiveAdCode || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {inq.ProdukInterest || "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          maxWidth: 180,
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {inq.LokasiInterest || ""}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{latestDealSales}</Typography>

                      <Typography variant="caption" color="text.secondary">
                        {latestDealStatus}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(inq.InqDate)}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Last: {formatDateTime(inq.lastActivityAt)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => openGradeDialog(inq)}
                        >
                          Grade
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openDetailDialog(inq)}
                        >
                          Detail
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Paper>
  );

  const renderGradeDialog = () => (
    <Dialog open={gradeOpen} onClose={closeGradeDialog} fullWidth maxWidth="sm">
      <DialogTitle>Manual Grade Inquiry</DialogTitle>

      <DialogContent>
        {selectedInquiry && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selectedInquiry.Nama || "-"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {selectedInquiry.Phone || "-"} | Current:{" "}
              {getStageLabel(selectedInquiry.leadStage)}
            </Typography>
          </Box>
        )}

        {!!gradeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {gradeError}
          </Alert>
        )}

        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Lead Stage</InputLabel>
            <Select
              label="Lead Stage"
              value={gradeStage}
              onChange={(e) => {
                setGradeStage(e.target.value);
                setDropReason("");
              }}
            >
              {LEAD_STAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedStageIsStage3 && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Assign Sales</InputLabel>
                <Select
                  label="Assign Sales"
                  value={manualSales}
                  onChange={(e) => setManualSales(e.target.value)}
                >
                  <MenuItem value="">Auto / Previous Sales</MenuItem>
                  {SALES_OPTIONS.filter(Boolean).map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifySales}
                    onChange={(e) => setNotifySales(e.target.checked)}
                  />
                }
                label="Notify sales via WhatsApp"
              />
            </>
          )}

          {(selectedStageNeedsBadReason || selectedStageIsStage2) && (
            <FormControl fullWidth size="small">
              <InputLabel>Drop Reason</InputLabel>
              <Select
                label="Drop Reason"
                value={dropReason}
                onChange={(e) => setDropReason(e.target.value)}
              >
                {currentDropOptions.map((opt) => (
                  <MenuItem key={opt.value || "empty"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            size="small"
            label="Reason"
            value={gradeReason}
            onChange={(e) => setGradeReason(e.target.value)}
            placeholder="Contoh: Manual review, client tanya harga PT"
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Notes"
            value={gradeNote}
            onChange={(e) => setGradeNote(e.target.value)}
            placeholder="Tulis alasan/observasi..."
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeGradeDialog} disabled={gradeSaving}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleGradeSubmit}
          disabled={gradeSaving}
        >
          {gradeSaving ? "Saving..." : "Save Grade"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDetailDialog = () => {
    const inq = detailData?.inquiry || {};
    const deals = detailData?.deals || [];
    const activities = detailData?.activities || [];
    const tasks = detailData?.tasks || [];

    return (
      <Dialog open={detailOpen} onClose={closeDetailDialog} fullWidth maxWidth="lg">
        <DialogTitle>Inquiry Detail</DialogTitle>

        <DialogContent>
          {detailLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading detail...</Typography>
            </Stack>
          )}

          {!!detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}

          {!detailLoading && detailData && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Inquiry
                  </Typography>

                  <Typography>Nama: {inq.Nama || "-"}</Typography>
                  <Typography>Phone: {inq.Phone || "-"}</Typography>
                  <Typography>Stage: {getStageLabel(inq.leadStage)}</Typography>
                  <Typography>
                    Sales: {inq.currentOwnerSales || inq.Sales || "-"}
                  </Typography>
                  <Typography>
                    AdCode:{" "}
                    {inq.qualificationAdCode ||
                      inq.latestAdCode ||
                      inq.firstAdCode ||
                      inq.AdCode ||
                      "-"}
                  </Typography>
                  <Typography>Produk: {inq.ProdukInterest || "-"}</Typography>
                  <Typography>Lokasi: {inq.LokasiInterest || "-"}</Typography>
                  <Typography>
                    Cekat: {inq.cekatGreetingStatus || "-"}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Deals / Opportunities
                  </Typography>

                  {deals.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Belum ada deal.
                    </Typography>
                  ) : (
                    deals.map((deal) => (
                      <Box
                        key={deal._id}
                        sx={{
                          borderBottom: "1px solid #eee",
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {deal.opportunityStatus || deal.status || "-"} |{" "}
                          {deal.Sales || "-"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDateTime(deal.dateCreated)} | FU:{" "}
                          {deal.salesFollowUpCounter ?? deal.counterFU ?? 0}
                        </Typography>

                        {!!deal.dropReason && (
                          <Typography variant="caption" color="error" display="block">
                            Drop: {deal.dropReason}
                          </Typography>
                        )}
                      </Box>
                    ))
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Activities
                  </Typography>

                  {activities.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Belum ada activity.
                    </Typography>
                  ) : (
                    activities.slice(0, 20).map((activity) => (
                      <Box
                        key={activity._id}
                        sx={{
                          borderBottom: "1px solid #eee",
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {activity.type || "-"} | {activity.outcome || "-"}
                        </Typography>

                        <Typography variant="body2">
                          {activity.note || "-"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(activity.createdAt)}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Tasks
                  </Typography>

                  {tasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Belum ada task.
                    </Typography>
                  ) : (
                    tasks.slice(0, 15).map((task) => (
                      <Box
                        key={task._id}
                        sx={{
                          borderBottom: "1px solid #eee",
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {task.title || "-"} | {task.status || "-"}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Due: {formatDateTime(task.dueAt)} | Sales:{" "}
                          {task.Sales || "-"}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Grid container>
      <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2}>
        <LeftMenu />
      </Grid>

      <Grid item xl={10.8} lg={10.6} md={10.4} sm={8.8} xs={12}>
        <Box sx={{ mt: 15 }} />

        <Box sx={{ pb: 4, px: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Inquiry Admin V2
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Grade legacy leads, monitor stage pipeline, and check Cekat failed
              greetings.
            </Typography>
          </Box>

          {renderModeTabs()}

          <Box sx={{ mt: 2 }} />

          {renderFilters()}

          {!!loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loadError}
            </Alert>
          )}

          {renderTable()}
        </Box>

        {renderGradeDialog()}
        {renderDetailDialog()}
      </Grid>
    </Grid>
  );
}